/**
 * Copyright 2026 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { useEffect, useState } from "react";
import { useLoadResourceStatesForAction } from "~/views/events/hooks";
import { useEventActionResourceCounts } from "~/views/events/hooks/useEventActionResourceCounts";

type Props = {
  eventId: string;
  allActionIds: string[];
  isExpanded: boolean;
  isEventFinished: boolean;
};

export function useEventItemResourceStates({
  eventId,
  allActionIds,
  isExpanded,
  isEventFinished,
}: Props) {
  const {
    updateResourceState,
    initializeActionCounts,
    clearAll,
    getCountsForAction,
  } = useEventActionResourceCounts();

  const loadResourceStatesForAction = useLoadResourceStatesForAction(
    eventId,
    initializeActionCounts
  );

  const [hasLoadedInitialStates, setHasLoadedInitialStates] = useState(false);

  useEffect(() => {
    return () => {
      clearAll();
    };
  }, [clearAll]);

  useEffect(() => {
    setHasLoadedInitialStates(false);
  }, [eventId]);

  useEffect(() => {
    if (
      !isExpanded ||
      hasLoadedInitialStates ||
      allActionIds.length === 0 ||
      isEventFinished
    ) {
      return;
    }

    void (async () => {
      await Promise.all(allActionIds.map(loadResourceStatesForAction));
      setHasLoadedInitialStates(true);
    })();
  }, [
    isExpanded,
    hasLoadedInitialStates,
    allActionIds,
    isEventFinished,
    loadResourceStatesForAction,
  ]);

  return {
    updateResourceState,
    getCountsForAction,
  } as const;
}
